<script>
import initAnalytics, { setSiteId, setPageName } from '../../utils/analytics';
import EditorsChoiceAwardIntroImage from './components/EditorsChoiceAwardIntroImage.vue';
import EditorsChoiceAwardMethodology from './components/EditorsChoiceAwardMethodology.vue';
import EditorsChoiceAwardRecent from './components/EditorsChoiceAwardRecent.vue';
import EditorsChoiceAwardSearch from './components/EditorsChoiceAwardSearch.vue';

import StickyComponent from '../../components/SubNav/StickyComponent.vue';
import SubNav from '../../components/SubNav/SubNav.vue';

export default {
  name: 'EditorsChoicePage',
  components: {
    EditorsChoiceAwardIntroImage,
    EditorsChoiceAwardMethodology,
    EditorsChoiceAwardRecent,
    EditorsChoiceAwardSearch,
    SubNav,
    StickyComponent,
  },
  inheritAttrs: false,
  props: {
    heroImageInfos: {
      type: Array,
      default: null,
    },
    title: {
      type: String,
      default: '',
    },
    intro: {
      type: Object,
      default: null,
    },
    mobileIntro: {
      type: Object,
      default: null,
    },
    promotedWinners: {
      type: Object,
      default: null,
    },
    searchableWinners: {
      type: Array,
      default: null,
    },
    methodologyModel: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      baseClassName: 'ea-editors-choice-awards',
      textMainHeader: 'REI Co-op Editors’ Choice Awards',
      textSearchHeader: 'All Award Winners',
      textMethodologyHeader: 'Awards Methodology',
      subNavLinks: [
        { name: 'Latest Winners', href: '#ea_LatestWinners' },
        { name: 'All Winners', href: '#ea_AllWinners' },
        { name: 'Methodology', href: '#ea_Methodology' },
      ],
    };
  },
  computed: {
    getHeroImage() {
      return this.heroImageInfos.find((x) => x.key === 'hero-image-large')?.heroImageInfo?.url;
    },
    richtextIntro() {
      return this.intro?.content;
    },
    richtextIntroMobile() {
      return this.mobileIntro?.content;
    },
    recentAwardWinners() {
      return this.promotedWinners.cards;
    },
  },
  mounted() {
    setSiteId('expert advice');
    setPageName('editors choice');
    initAnalytics('#ea_editors-choice-page');

    // add support for contentStack live preview
    window.addEventListener('OnLivePreviewUpdate', () => {
      this.$emit('OnLivePreviewUpdate');
    });
  },
};
</script>

<template>
  <div
    id="ea_editors-choice-page"
    :class="`${baseClassName}`"
  >
    <editors-choice-award-intro-image
      :hero-image="getHeroImage"
      :hero-image-alt-text="getHeroAltText"
      :content-intro="richtextIntro"
      :content-intro-mobile="richtextIntroMobile"
      :header-content="textMainHeader"
      header-id="ea_LatestWinners"
    />

    <sticky-component>
      <sub-nav
        :class="`${baseClassName}__subnav`"
        :links="subNavLinks"
      />
    </sticky-component>

    <editors-choice-award-recent :award-winners="recentAwardWinners" />

    <div
      id="ea_AllWinners"
      :class="`${baseClassName}__header_container`"
    >
      <h2 :class="`${baseClassName}__header`">
        {{ textSearchHeader }}
      </h2>
    </div>

    <editors-choice-award-search :award-winners="searchableWinners[0].cards" />

    <section id="ea_Methodology">
      <div
        :class="`${baseClassName}__header_container`"
      >
        <h2 :class="`${baseClassName}__header`">
          {{ textMethodologyHeader }}
        </h2>
      </div>

      <editors-choice-award-methodology :content="methodologyModel?.copy?.json" />
    </section>
  </div>
</template>

<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';
@import '../../../style/common/editor-styles.scss';

$baseClassName: 'ea-editors-choice-awards';

#ea_Methodology {
  .#{$baseClassName}__header {
    text-transform: uppercase;
    @include cdr-text-utility-sans-strong-500;
    @include cdr-xs-mq-only {
      @include cdr-text-utility-sans-strong-300;
    }
  }
  background-color: $cdr-color-background-primary;
  border-radius: $cdr-radius-softer;

  @include cdr-xs-mq-only {
    margin-bottom: $cdr-space-half-x;
    margin: 0 4vw;
  }
  @include cdr-sm-mq-only {
    margin-bottom: $cdr-space-one-x;
    margin: 0 6vw;
  }
  @include cdr-md-mq-only {
    margin-bottom: $cdr-space-two-x;
    margin: 0 6vw;
  }
  @include cdr-lg-mq-up {
    margin-bottom: $cdr-space-four-x;
    @include cdr-container;
  }
}

.#{$baseClassName} {
  background-color: $cdr-color-background-secondary;
  padding-bottom: $cdr-space-two-x;
}

.#{$baseClassName}__subnav {
  @include cdr-xs-mq-only {
    background-color: $cdr-color-background-secondary;
  }
  @include cdr-sm-mq-up {
    background-color: $cdr-color-background-primary;
  }
}

.#{$baseClassName}__header_container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: $cdr-space-one-x $cdr-space-one-x $cdr-space-half-x $cdr-space-one-x;
  padding-top: $cdr-space-two-x;
}
#ea_AllWinners {
  .#{$baseClassName}__header {
    @include cdr-xs-mq-only {
      text-transform: uppercase;
      @include cdr-text-utility-sans-strong-300;
    }
    @include cdr-sm-mq-only {
      @include cdr-text-heading-display-1000;
    }
    @include cdr-md-mq-only {
      @include cdr-text-heading-display-1200;
    }
    @include cdr-lg-mq-up {
      @include cdr-text-heading-display-1400;
    }
  }
}

.#{$baseClassName}__page_desc {
  @include cdr-xs-mq-only {
    @include cdr-text-body-400;
  }
  @include cdr-sm-mq-up {
    display: none;
  }
}

</style>
